import * as React from 'react';
import { useField, Field, FormikHandlers, FieldValidator } from 'formik';
import getPressedKey, { KeyboardMap } from '../helpers/getPressedKey';

interface Input {
  name: string;
  type: string;
  id?: string;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: FormikHandlers['handleChange'];
  onBlur?: FormikHandlers['handleBlur'];
  onFocus?: React.FocusEventHandler;
  validate?: FieldValidator;
  className?: string;
  autoComplete?: string;
  digitsOnly?: boolean;
  showCurrency?: boolean;
  placeholder?: string;
  innerRef?: React.ForwardedRef<any>;
}

const Input = ({
  id,
  name,
  type,
  min,
  max,
  step,
  className = '',
  label,
  autoComplete = 'on',
  required = false,
  disabled = false,
  onChange,
  onBlur,
  validate,
  onFocus,
  digitsOnly = false,
  showCurrency = false,
  placeholder,
  innerRef
}: Input): React.ReactElement => {
  const [field, meta] = useField(name);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const matchDigits = /[0-9]/.test(e.key);
    const matchEnter = getPressedKey(e) === KeyboardMap.ENTER;

    if (!matchDigits && !matchEnter) {
      e.preventDefault();
    }
  };

  return (
    <div className={`input ${meta.touched && meta.error ? 'input--has-error' : ''}`}>
      <Field
        type={type}
        id={id || field.name}
        name={field.name}
        onBlur={onBlur || field.onBlur}
        onChange={onChange || field.onChange}
        onKeyPress={digitsOnly ? handleKeyPress : null}
        className={`input__field ${className} 
          ${field.value ? 'input__field--has-value' : ''} 
          ${showCurrency ? ' input__field--currency' : ''}`}
        disabled={disabled}
        validate={validate}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete}
        onFocus={onFocus}
        required={required}
        placeholder={placeholder}
        innerRef={innerRef}
      />
      {showCurrency && <span className="input__currency">{window.bbGlobal.currentCurrencySymbol}</span>}
      <label className="input__label" htmlFor={id || field.name}>
        {label}
      </label>
      {meta.touched && meta.error && (
        <p className="input__error" role="alert">
          {meta.error}
        </p>
      )}
    </div>
  );
};

export default Input;
